import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast, { Toaster } from "react-hot-toast";
import {
  FaGraduationCap, FaUser, FaEnvelope, FaPhone,
  FaIdCard, FaBuilding, FaCalendarAlt, FaSignOutAlt
} from "react-icons/fa";
import { FiBook, FiAward, FiHome, FiCode } from "react-icons/fi";
import DSASheet from "../Student/DSASheet";
import ThemeToggle from "../../components/ThemeToggle";

const AlumniHome = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("Dashboard");

  useEffect(() => {
    if (!router.state) {
      navigate("/");
      return;
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.post(
        `${baseApiURL()}/student/details/getDetails`,
        { enrollmentNo: router.state.enrollmentNo || router.state.loginid },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        setData(response.data.user[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = () => {
    navigate("/");
    toast.success("Logged out successfully");
  };

  const menuItems = [
    { name: "Dashboard", icon: FiHome },
    { name: "DSA Sheet", icon: FiCode },
    { name: "Profile", icon: FaUser },
  ];

  const renderContent = () => {
    if (selectedMenu === "DSA Sheet") {
      return <DSASheet userType="Alumni" />;
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      );
    }

    if (selectedMenu === "Profile") {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm dark:shadow-xl overflow-hidden text-slate-900 dark:text-slate-100">
            <div className="bg-gradient-to-r from-indigo-50 via-slate-50 to-indigo-50 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 p-8 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-white text-3xl font-bold border-4 border-indigo-200 dark:border-indigo-500/30">
                  {data.firstName?.[0]}{data.lastName?.[0]}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {[data.firstName, data.middleName, data.lastName].filter(Boolean).join(" ")}
                  </h2>
                  <p className="text-indigo-600 dark:text-indigo-300">Alumni • Batch {data.batch}</p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 mt-2">
                    🎓 Graduated {data.graduationYear || new Date().getFullYear()}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "Enrollment No", value: data.enrollmentNo, icon: FaIdCard },
                { label: "Branch", value: data.branch, icon: FaBuilding },
                { label: "Email", value: data.email, icon: FaEnvelope },
                { label: "Phone", value: data.phoneNumber, icon: FaPhone },
                { label: "Regulation", value: data.regulation, icon: FiBook },
                { label: "Gender", value: data.gender, icon: FaUser },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="flex items-start space-x-3 p-3.5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon size={14} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm mt-0.5">{value || "N/A"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Dashboard
    return (
      <div className="space-y-6">
        {/* Welcome Banner */}
        <div className="bg-white dark:bg-gradient-to-r dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950 rounded-2xl p-6 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 shadow-xs dark:shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-600 dark:text-indigo-400 text-sm font-semibold mb-1">Welcome back, Alumni</p>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                {data ? [data.firstName, data.lastName].filter(Boolean).join(" ") : "Alumni"}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                {data?.branch} • Batch {data?.batch} • {data?.regulation}
              </p>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-2xl p-4 text-indigo-600 dark:text-indigo-400">
              <FaGraduationCap className="text-5xl" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Enrollment No", value: data?.enrollmentNo || "—", icon: FaIdCard, color: "indigo" },
            { label: "Branch", value: data?.branch || "—", icon: FaBuilding, color: "emerald" },
            { label: "Batch", value: data?.batch || "—", icon: FaCalendarAlt, color: "amber" },
            { label: "Graduation Year", value: data?.graduationYear || new Date().getFullYear(), icon: FiAward, color: "rose" },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-md p-4 flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20">
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
                <p className="font-bold text-slate-900 dark:text-white text-sm mt-0.5">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Graduation Card */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 flex items-center justify-center">
              <FaGraduationCap size={20} />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Alumni Status</h3>
          </div>
          <div className="bg-emerald-50/50 dark:bg-slate-950 border border-emerald-200 dark:border-slate-800 rounded-xl p-5 flex items-center space-x-4">
            <div className="text-4xl">🎓</div>
            <div>
              <p className="font-bold text-emerald-700 dark:text-emerald-400 text-lg">Congratulations, Graduate!</p>
              <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                You have successfully completed your degree from <strong className="text-slate-900 dark:text-white">{data?.branch}</strong>.
                Your alumni credentials grant you access to the ECAP Alumni Portal & DSA Coding Practice.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs dark:shadow-md p-6">
          <h3 className="font-bold text-slate-900 dark:text-white text-lg mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <FaEnvelope className="text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{data?.email || "Not provided"}</span>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <FaPhone className="text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{data?.phoneNumber || "Not provided"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col transition-colors duration-250">
      <Toaster position="top-right" />

      {/* Top Navbar */}
      <nav className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white px-6 py-4 shadow-xs dark:shadow-lg flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center">
            <FaGraduationCap className="text-xl" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">Alumni Portal</h1>
            <p className="text-indigo-600 dark:text-indigo-400 text-xs font-semibold">ECAP Alumni Network</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <ThemeToggle />
          <span className="text-xs text-slate-600 dark:text-slate-400 font-mono bg-slate-100 dark:bg-slate-900 px-3 py-1 rounded-full border border-slate-200 dark:border-slate-800">
            {router.state?.loginid}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-500 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white transition-all cursor-pointer shadow-md shadow-indigo-600/30"
          >
            <FaSignOutAlt className="text-xs" />
            <span>Logout</span>
          </button>
        </div>
      </nav>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col py-6 px-3">
          {menuItems.map(({ name, icon: Icon }) => (
            <button
              key={name}
              onClick={() => setSelectedMenu(name)}
              className={`flex items-center space-x-3 px-4 py-3 rounded-xl mb-1.5 transition-all font-bold text-xs cursor-pointer ${
                selectedMenu === name
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Icon size={16} />
              <span>{name}</span>
            </button>
          ))}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto bg-slate-50 dark:bg-slate-950">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AlumniHome;
